import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  AuthTokens,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
} from '../../models/auth.model';
import { TokenService } from './token.service';
import { SessionService } from '../session.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);
  private readonly sessionService = inject(SessionService);
  private readonly apiUrl = environment.apiUrl;

  /**
   * Authenticate user with email and password (+ optional MFA code).
   * Stores the token pair; the full profile is loaded afterwards via
   * SessionService.loadProfile() (GET /auth/me).
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}auth/login`, credentials)
      .pipe(tap((response) => this.storeTokens(response.data.tokens)));
  }

  /**
   * Rotate the refresh token and obtain a new access token.
   * API contract: POST /auth/refresh → { status, data: { user, tokens } }.
   */
  refreshTokens(): Observable<RefreshTokenResponse> {
    const refreshToken = this.tokenService.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const payload: RefreshTokenRequest = { refreshToken };

    return this.http.post<RefreshTokenResponse>(`${this.apiUrl}auth/refresh`, payload).pipe(
      tap((response) => {
        const tokens = response?.data?.tokens;
        if (!tokens?.accessToken || !tokens?.refreshToken) {
          throw new Error('Invalid refresh token response structure');
        }
        this.storeTokens(tokens);
      }),
    );
  }

  /**
   * Logout: revoke the refresh token server-side (fire-and-forget) and
   * clear all local auth/session state.
   */
  logout(): void {
    const refreshToken = this.tokenService.getRefreshToken();
    if (refreshToken) {
      this.http
        .post(`${this.apiUrl}auth/logout`, { refreshToken })
        .subscribe({ error: () => undefined });
    }
    this.tokenService.clearTokens();
    this.sessionService.clear();
  }

  /**
   * Check if user is authenticated (valid access token + loaded profile).
   */
  isAuthenticated(): boolean {
    return this.tokenService.hasValidAccessToken() && this.sessionService.current !== null;
  }

  private storeTokens(tokens: AuthTokens): void {
    const expiresAt = new Date(Date.now() + tokens.accessExpiresIn * 1000).toISOString();
    this.tokenService.setAccessToken(tokens.accessToken, expiresAt);
    this.tokenService.setRefreshToken(tokens.refreshToken, undefined);
  }
}
